import React from 'react';
import AdminDashboardSkeleton from '../components/loading/AdminDashboardSkeleton';
import AdminListTableSkeleton from '../components/loading/AdminListTableSkeleton';
import AdminClientDetailSkeleton from '../components/loading/AdminClientDetailSkeleton';
import TargetInvestorsTableSkeleton from '../components/loading/TargetInvestorsTableSkeleton';
import ClientsSkeleton from '../components/admin/ClientsSkeleton';
import AdminLeadsPageSkeleton from '../components/loading/AdminLeadsPageSkeleton';
import PipelinePageSkeleton from '../components/loading/PipelinePageSkeleton';
import { ListSkeleton } from '../components/loading';
import ProfileSkeleton from '../components/profile/ProfileSkeleton';
import InvestorsSkeleton from '../components/loading/InvestorsSkeleton';
import VoloceanLoader from '../components/loading/VoloceanLoader';
import SkeletonBlock from '../components/ui/SkeletonBlock';
import SkeletonRoot from '../components/ui/SkeletonRoot';

function AdminSyndicationDealsSkeleton() {
  return (
    <div className="min-h-screen bg-white p-4 sm:p-6">
      <div className="mx-auto max-w-[1400px] space-y-4">
        <AdminListTableSkeleton
          showPageShell={false}
          filterSlotCount={2}
          rowCount={6}
          loadingLabel="Loading syndication deals"
        />
      </div>
    </div>
  );
}

function AdminSyndicationDealDetailSkeleton() {
  return (
    <SkeletonRoot loadingLabel="Loading syndication deal">
      <div className="min-h-screen bg-white p-4 sm:p-6">
        <div className="mx-auto max-w-[1400px] space-y-6">
          <SkeletonBlock className="h-4 w-40 rounded" />
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-2">
              <SkeletonBlock className="h-8 w-64 rounded-lg" />
              <SkeletonBlock className="h-4 w-48 rounded" />
            </div>
            <div className="flex gap-2">
              <SkeletonBlock className="h-8 w-24 rounded-md" />
              <SkeletonBlock className="h-8 w-16 rounded-md" />
              <SkeletonBlock className="h-8 w-20 rounded-md" />
            </div>
          </div>
          <div className="flex gap-4 border-b border-neutral-200 pb-2">
            <SkeletonBlock className="h-8 w-24 rounded" />
            <SkeletonBlock className="h-8 w-32 rounded" />
            <SkeletonBlock className="h-8 w-20 rounded" />
          </div>
          <SkeletonBlock className="h-64 w-full rounded-2xl" />
        </div>
      </div>
    </SkeletonRoot>
  );
}

function AdminPipelineSkeleton() {
  return <PipelinePageSkeleton loadingLabel="Loading pipeline" />;
}

function AdminPartnersSkeleton() {
  return <ListSkeleton rows={7} showSearch showFilters title="Loading partners..." />;
}

function AdminAnalyticsSkeleton() {
  return (
    <ListSkeleton rows={6} showSearch={false} showFilters={false} title="Loading analytics sessions..." />
  );
}

function AdminLeadsSkeleton() {
  return <AdminLeadsPageSkeleton loadingLabel="Loading leads" />;
}

/**
 * Route-level Suspense fallback keyed by pathname.
 * Most specific matches first to avoid double-flash with VoloceanLoader.
 */
export function getRouteSkeletonFallback(pathname: string): React.ReactNode {
  if (pathname.endsWith('/target-investors')) {
    return <TargetInvestorsTableSkeleton />;
  }

  if (/^\/admin\/clients\/[^/]+$/.test(pathname)) {
    return <AdminClientDetailSkeleton />;
  }

  if (pathname === '/admin/clients' || pathname === '/admin/delivery' || pathname === '/admin/delivery/modular') {
    return <ClientsSkeleton />;
  }

  if (/^\/admin\/syndication-deals\/[^/]+$/.test(pathname)) {
    return <AdminSyndicationDealDetailSkeleton />;
  }

  if (pathname === '/admin/syndication-deals') {
    return <AdminSyndicationDealsSkeleton />;
  }

  if (pathname === '/admin/investors' || pathname === '/admin/syndicates') {
    return <InvestorsSkeleton />;
  }

  if (pathname === '/admin/leads') {
    return <AdminLeadsSkeleton />;
  }

  if (pathname === '/admin/pipeline' || pathname.startsWith('/admin/pipeline/')) {
    return <AdminPipelineSkeleton />;
  }

  if (pathname === '/admin/partners') {
    return <AdminPartnersSkeleton />;
  }

  if (pathname === '/admin/analytics') {
    return <AdminAnalyticsSkeleton />;
  }

  if (pathname === '/admin') {
    return <AdminDashboardSkeleton />;
  }

  if (pathname === '/profile') {
    return <ProfileSkeleton />;
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-white">
      <VoloceanLoader size="lg" message="Loading page..." />
    </div>
  );
}
